<template>
  <form class="ONE-TIME-CODE-FORM p-x-2" @submit.prevent="submit">
    <div class="h4 text-bold m-b-1">
      Sign In With Contact Info
    </div>
    <div class="m-b-3 INSTRUCTION-TEXT" >
      To receive a one-time sign in code,
      enter the contact info you used when you requested a service.
      If you haven’t requested a service yet,
      <a href="/" class="link">visit our homepage</a>
      to submit a job request before signing in.
    </div>
    <div
        :error="emailAddressMessage"
        :class="{'error': hasMultipleAccounts}"
        class="input-container m-b-2">
      <div
          :class="{ 'error': (validation.hasError('email') && !disableEmail) }"
          :error="validation.firstError('email')"
          class="input-container"
      >
        <input
            v-vk-input
            v-model="email"
            class="input"
            type="text"
            :disabled="loading || disableEmail">
        <label class="input-label">Email</label>
        <div
            :error="notFoundMessage"
            :class="{'error': noAccountsEmail}"
            class="input-container"></div>
      </div>
    </div>
    <div class="d-flex p-b-4">
      <hr class="flex-1-1 m-r-1 bd-b-gray40"/>Or<hr class="flex-1-1 m-l-1 bd-b-gray40"/>
    </div>
    <div
        :error="notFoundMessage"
        :class="{'error': noAccountsPhone}"
        class="input-container m-b-2">
      <div
          :class="{ 'error': (validation.hasError('phone') && !disablePhone)}"
          :error="validation.firstError('phone')"
          class="input-container m-b-2 "
      >
        <vk-phone-input
            :disabled="loading || disablePhone"
            v-model="phone"
            class="input"
        />
        <label class="input-label">Cell Phone Number</label>
      </div>
    </div>
    <div
        v-if="hasApiError"
        class="c-red m-b-2">
      <vk-icon
          name="exclamation"
          class="ERROR-ICON"/>
      Oops! We’re unable to create a code right now. Please try again later.
    </div>
    <button
        :disabled="loading || disabled"
        type="submit"
        class="btn w-100 m-y-2"
    >
      Send Me a One-Time Code
    </button>
  </form>
</template>

<script>
import axios from 'axios';
import SimpleVueValidator, { Validator } from 'simple-vue-validator';
import SpecialMessage from '@/components/SpecialMessage';


export default {
  name: 'one-time-code-form',
  components: {
    SpecialMessage,
  },
  mixins: [SimpleVueValidator.mixin],
  props: {
    redirectTo: {
      type: [Object, String],
      default: null,
    },
    defaultEmail: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      email: this.defaultEmail,
      loading: false,
      phone: '',
      accounts: [],
      hasAttemptedSubmission: false,
      hasAttemptedVerifyEmail: false,
      sentTo: undefined,
      emailOrPhone: '',
      timeStarted: undefined,
      emailMatchesPhone: false,
      hasApiError: false,
      hasNoAccounts: false,
      notFoundMessage: '',
      emailAddressMessage: '',
      hasMultipleAccounts: false,

    };
  },
  computed: {
    disabled () {
      return this.hasAttemptedVerifyEmail && this.hasMultipleAccounts;
    },
    activeInput () {
      return this.email ? 'email' : 'phone';
    },
    noAccountsEmail () {
      return (this.hasNoAccounts && this.activeInput === 'email') || (this.hasAttemptedVerifyEmail);
    },
    noAccountsPhone () {
      return this.hasNoAccounts && this.activeInput === 'phone' && !this.loading;
    },
    disablePhone () {
      return !!this.email || this.hasMultipleAccounts;
    },
    contactInfo () {
      return {
        email: this.email,
        phone: this.phone,
      };
    },
    disableEmail () {
      return !!this.phone && !this.accounts.length;
    },
  },
  validators: {
    email (value) {
      return Validator.value(value)
        .required('Please enter your email address.')
        .email('Please enter a valid email address.');
    },
    phone: (value) => {
      return Validator.value(value).required().digit().minLength(10);
    },
  },
  watch: {
    email (value) {
      if (value === '') {
        this.hasAttemptedSubmission = false;
        this.validation.reset();
      }
    },
    phone (value) {
      if (value === '') {
        this.hasAttemptedSubmission = false;
        this.validation.reset();
      }
    },
  },
  methods: {
    async submit () {
      // Validating inputs and determining
      // if we're using phone or email
      const valid = await this.$validate(
        Object.keys(this.contactInfo)
          .filter(key => this.contactInfo[key] === this.activeInput));
      this.sentTo = this.email ? this.email : this.phone;
      this.emailOrPhone = this.email ? 'Sent to email' : 'Sent to phone';

      // Starting 15min timer for the component to accept
      // the code that would have been sent
      this.timeStarted = Number(new Date().getTime());

      this.hasApiError = false;
      if (valid) {
        await this.getOneTimeCode();
        this.hasAttemptedSubmission = true;
        this.validation.reset();
        this.loading = false;
        this.$events.$emit(this.$events.OTC_REQUEST_CLICK, { emailOrPhone: this.emailOrPhone });
      }
    },
    async getOneTimeCode () {
      try {
        const { data: { login_identifier } } = await axios.post('oneTimeCode', {
          ...this.contactInfo,
        });
        this.$router.push({
          name: 'one-time-code-sign-in',
          params: {
            redirectTo: this.redirectTo,
            sentTo: this.sentTo,
            timeStarted: this.timeStarted,
            loginId: login_identifier,
          },
        });
      } catch (err) {
        if (err.response) {
          switch (err.response.status) {
            case 404:
              this.notFoundMessage = err.response.data.message;
              this.hasNoAccounts = true;
              break;
            case 400:
              this.emailAddressMessage = err.response.data.message;
              this.hasMultipleAccounts = true;
              break;
            default:
              console.error(err.response);
              break;
          }
        }
        this.hasApiError = true;
        this.$events.$emit(
          this.$events.OTC_REQUEST_CLICK_ERROR,
          { emailOrPhone: this.emailOrPhone }
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@homeservices/design-kit/tools.scss";

.ONE-TIME-CODE-FORM {
  @include responsive(width, 100%, 660px);
}
.INSTRUCTION-TEXT {
  font-size: 14px;
}
.ERROR-ICON {
  top: 4px;
  position: relative;
  line-height: 18px;
  padding: 4px;
  font-size: 12px;
  background-color: $color-red;
  color: white;
}
</style>
